import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { useContent } from "../content/ContentContext";
import { Reveal } from "./Reveal";
import { WatchModal } from "./WatchModal";
import { PremiereCountdown } from "./PremiereCountdown";

export const LatestEpisode = () => {
  const ref = useRef(null);
  const [watching, setWatching] = useState(null);
  const { LATEST_EPISODE } = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="latest" ref={ref} className="relative py-28 md:py-44 overflow-hidden" data-testid="latest-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src={LATEST_EPISODE.img}
          alt={LATEST_EPISODE.title}
          className="w-full h-full object-cover img-cinematic scale-110"
          animate={{ scale: [1.1, 1.16] }}
          transition={{ duration: 22, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          data-testid="latest-background-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ink/75" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/50 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="overline-label mb-5" data-testid="latest-overline">Latest conversation</p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-display text-sm text-gold-light/80 tracking-[0.3em] mb-4" data-testid="latest-ep-num">
            EPISODE {String(LATEST_EPISODE.num).padStart(2, "0")}
          </p>
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl"
            data-testid="latest-title"
          >
            {LATEST_EPISODE.title}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-sm md:text-base text-smoke max-w-xl mt-6 leading-relaxed" data-testid="latest-synopsis">
            {LATEST_EPISODE.synopsis}
          </p>
        </Reveal>
        {LATEST_EPISODE.premiere && !LATEST_EPISODE.videoId && (
          <Reveal delay={0.25}>
            <PremiereCountdown
              date={LATEST_EPISODE.premiere}
              label="Premieres Monday, 17 August 2026 — 12 PM"
              className="mt-9"
            />
          </Reveal>
        )}
        <Reveal delay={0.3}>
          <button
            onClick={() => setWatching(LATEST_EPISODE)}
            className="mt-10 inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-ink text-[11px] md:text-xs font-semibold tracking-[0.24em] uppercase px-9 py-4 rounded-full transition-colors duration-300"
            data-testid="latest-watch-button"
          >
            <Play size={13} className="fill-current" />
            {LATEST_EPISODE.videoId ? "Watch now" : LATEST_EPISODE.teaser ? "Watch the teaser" : "Watch now"}
          </button>
        </Reveal>
      </div>

      <WatchModal episode={watching} open={!!watching} onOpenChange={(o) => !o && setWatching(null)} onSelectEpisode={setWatching} />
    </section>
  );
};
